import {
    Group,
    Select,
    type SelectComboboxItem,
    type SelectProps,
    Text,
} from '@phx/design-system';
import { useTranslation } from 'react-i18next';

import { getFragments } from '../../graphql';
import type { MedicationFormInfoFragment } from '../../graphql/generated/graphql';
import { useFlags } from '../../hooks/use-flags';

type SelectFormOption = SelectComboboxItem & {
    formTranslation: string;
};

const renderFormOption: SelectProps['renderOption'] = ({ option }) => {
    const customOption = option as SelectFormOption;
    const { label, formTranslation } = customOption;

    const routeOfAdministration = label.endsWith(formTranslation)
        ? label.slice(0, label.length - formTranslation.length).trim()
        : '';

    return (
        <Group>
            <Text>
                {routeOfAdministration && routeOfAdministration + ' '}
                <Text span fw={600}>
                    {formTranslation}
                </Text>
            </Text>
        </Group>
    );
};

export const MedicationFormSelect = ({
    forms,
    selectedForm,
    setSelectedForm,
    setMedicationId,
}: {
    forms: MedicationFormInfoFragment[];
    selectedForm?: MedicationFormInfoFragment;
    setSelectedForm: (value: MedicationFormInfoFragment) => void;
    setMedicationId: (value: string) => void;
}) => {
    const { t } = useTranslation(['myphx', 'code-locales/form-code-mapping']);
    const { displayRouteOfAdministration } = useFlags();

    // count how many times a formCode appears
    const formCodeCounts = forms.reduce(
        (acc, form) => {
            acc[form.formCode] = (acc[form.formCode] || 0) + 1;
            return acc;
        },
        {} as Record<string, number>
    );

    const sortedOptions: SelectFormOption[] = forms
        .map((form) => {
            const isDuplicate = formCodeCounts[form.formCode] > 1;

            const formTranslation = t(form.formCode, {
                ns: 'code-locales/form-code-mapping',
                count: 1,
            });

            const label = isDuplicate
                ? `${form.routeOfAdministrationText} ${formTranslation}`
                : formTranslation;

            const sortKey = isDuplicate
                ? `${formTranslation} ${form.routeOfAdministrationText}`
                : formTranslation;

            return {
                value: JSON.stringify(form),
                label,
                sortKey,
                formTranslation,
            };
        })
        .sort((a, b) => a.sortKey.localeCompare(b.sortKey));

    const options = forms.map((form) => {
        return {
            value: JSON.stringify(form),
            label: t(form.formCode, {
                ns: 'code-locales/form-code-mapping',
                count: 1,
            }),
        };
    });

    const data = displayRouteOfAdministration ? sortedOptions : options;

    const renderOption = displayRouteOfAdministration
        ? renderFormOption
        : undefined;

    const onChange: (value: string | null) => void = (value) => {
        if (value) {
            const form = JSON.parse(value) as MedicationFormInfoFragment;
            const strengths = getFragments(form.strengths);
            const selectedMedicationId = strengths
                .map((strength) => getFragments(strength.packages))
                .flat()
                .find((pkg) => pkg.id)?.id;

            selectedMedicationId && setMedicationId(selectedMedicationId);

            setSelectedForm(JSON.parse(value));
        }
    };

    return (
        <Select
            placeholder="-"
            disabled={forms.length <= 1}
            label={t('drugConfig.form')}
            value={JSON.stringify(selectedForm)}
            onChange={onChange}
            data={data}
            renderOption={renderOption}
        />
    );
};
